import { useRef, useState } from "react";
import { XIcon, UploadCloudIcon } from "lucide-react";
import { Button } from "./Buttons";

const TYPE_OPTIONS = [
  { value: "xlsx", label: "Excel File (.xlsx / .csv)", accept: ".xlsx,.xls,.csv" },
  { value: "json", label: "JSON Payload (paste)" },
];

const ExcelImportModal = ({ open, onClose, onSubmit, loading = false }) => {
  const [type, setType] = useState("xlsx");
  const [file, setFile] = useState(null);
  const [jsonText, setJsonText] = useState("");
  const fileRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setType("xlsx");
    setFile(null);
    setJsonText("");
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const submit = () => {
    if (type === "xlsx") {
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", "xlsx");
      onSubmit?.({ type, body: fd });
      return;
    }
    let parsed;
    try {
      parsed = jsonText ? JSON.parse(jsonText) : {};
    } catch {
      parsed = { raw: jsonText };
    }
    onSubmit?.({ type, body: { type: "json", data: parsed } });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-slate-800 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Import Data</h3>
          <button type="button" onClick={close} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon size={18} />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Data Type</label>
            <div className="mt-2 flex flex-col gap-2">
              {TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="radio"
                    name="excel-type"
                    value={opt.value}
                    checked={type === opt.value}
                    onChange={() => setType(opt.value)}
                    className="text-green-500 focus:ring-green-400"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {type === "xlsx" ? (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-600 px-4 py-6 text-sm text-gray-500 hover:border-green-400 hover:text-green-500"
              >
                <UploadCloudIcon size={18} />
                {file ? file.name : "Click to choose file"}
              </button>
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">JSON Payload</label>
              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                rows={6}
                placeholder='[{ "name": "..." }]'
                className="mt-2 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-400"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-slate-700 px-4 py-3">
          <Button type="button" onClick={close} className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-700 dark:!text-gray-200" disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            className="!bg-green-500 !text-white hover:!bg-green-600"
            disabled={loading || (type === "xlsx" && !file)}
            loading={loading}
            loadingText="Importing..."
          >
            Import
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;
